import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate, useSearch } from '@tanstack/react-router'
import type { AxiosError } from 'axios'

import { Alert } from '@gto/ui/alert'
import { IconRakebackWizardLogoSigned } from '@gto/ui/assets'
import { Button } from '@gto/ui/button'
import { Checkbox } from '@gto/ui/checkbox'
import { Paper } from '@gto/ui/paper'
import { Typography } from '@gto/ui/typography'

import { useAccountPartialUpdate } from '@/api/gen'
import { useAuth } from '@/hooks/use-auth'
import { getInitialLanguage } from '@/localise'
import { PrivacyAgreementLabel } from '@/pages/auth/components/privacy-agreement-label'
import { store } from '@/store'
import { getAxiosErrorMessage, oAuthMigrationUrlFlag } from '@/utils/helpers'

export const OauthNewUser = () => {
  const navigate = useNavigate()
  const { isInitialized, isAuthorized, termsAccepted, user } = useAuth()

  useEffect(
    function redirectAuthorizedUserEffect() {
      if (isInitialized && isAuthorized && termsAccepted) {
        navigate({ to: '/app/rewards', replace: true }).catch(console.error)
      }
    },
    [isInitialized, isAuthorized, termsAccepted, user?.term_and_condition, navigate],
  )

  if (!isInitialized || !user) {
    return null
  }

  return <OAuthNewUserForm />
}

const OAuthNewUserForm = () => {
  const { messages } = useIntl()
  const { formatMessage } = useIntl()
  const search = useSearch({
    from: '/_auth',
  })
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  const { mutate: updateAccount, isPending } = useAccountPartialUpdate({
    mutation: {
      onError: (error: AxiosError) => {
        const msg = getAxiosErrorMessage(error)
        setError(msg || formatMessage({ id: 'auth.oauth.error' }))
      },
      onSuccess: async () => {
        sessionStorage.removeItem('new_user_flow')
        await store.getState().refetchUser()

        if (search.back_url === oAuthMigrationUrlFlag) {
          navigate({
            to: '/login/oauth/link-old-account',
          }).catch(console.error)
        } else {
          store.getState().initialize().catch(console.error)
        }
      },
    },
  })

  const [formValues, setFormValues] = useState({
    terms: false,
    shareInfoToGto: false,
    allow_marketing_emails: true,
  })

  const onCheckboxChange = (name: keyof typeof formValues, checked: boolean) => {
    setFormValues((prev) => ({ ...prev, [name]: checked }))
  }

  const onSubmit = () => {
    setError(null)
    updateAccount({
      data: {
        term_and_condition: true,
        allow_marketing_emails: formValues.allow_marketing_emails,
        preferred_language: getInitialLanguage(),
      },
    })
  }

  const isAllChecked = formValues.terms && formValues.shareInfoToGto

  const raw = messages['auth.signup.shareInfoToGto.description'] as unknown
  const description = Array.isArray(raw) ? (raw as string[]) : []

  return (
    <div className="flex size-full flex-col items-center justify-center gap-6 p-4">
      <IconRakebackWizardLogoSigned height={50} />
      <Paper
        className="w-full max-w-[500px] rounded-md border border-divider p-6"
        elevation="2"
      >
        <div className="flex flex-col gap-8">
          <div className="text-center">
            <Typography
              component="p"
              variant="h4"
            >
              <FormattedMessage id="auth.oauth.newUser.title" />
            </Typography>
            {description.length > 0 && (
              <Typography
                color="text.secondary"
                variant="body1"
              >
                <FormattedMessage id="auth.oauth.newUser.description" />
              </Typography>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Checkbox
              required
              checked={formValues.terms}
              className="h-auto"
              label={<PrivacyAgreementLabel />}
              onCheckedChange={(checked) => onCheckboxChange('terms', checked)}
            />
            <Checkbox
              checked={formValues.shareInfoToGto}
              className="h-auto"
              label={
                <div>
                  <span>
                    <FormattedMessage id="auth.signup.shareInfoToGto" />
                    <span className="ml-1 text-error-main">*</span>
                  </span>
                  <ul className="m-0 ml-2 list-inside list-disc p-0">
                    {description.map((term) => (
                      <li key={term}>{term}</li>
                    ))}
                  </ul>
                </div>
              }
              onCheckedChange={(checked) => onCheckboxChange('shareInfoToGto', checked)}
            />
            <Checkbox
              checked={formValues.allow_marketing_emails}
              className="h-auto"
              label={<FormattedMessage id="auth.signup.emailSubscribe" />}
              onCheckedChange={(checked) => onCheckboxChange('allow_marketing_emails', checked)}
            />
          </div>
          {error !== null && (
            <Alert
              heading={error}
              severity="warning"
            />
          )}
          <Button
            color="primary"
            disabled={!isAllChecked || isPending}
            loading={isPending}
            size="large"
            variant="contained"
            onClick={onSubmit}
          >
            <FormattedMessage id="common.controls.createAccount" />
          </Button>
        </div>
      </Paper>
    </div>
  )
}
